import React, { useCallback } from 'react'
import tw from 'twin.macro'
import { Divider, Typography } from '@mui/material'
import { LinkMinor } from '@shopify/polaris-icons'
import { BasicModalDescription, BasicModalHeader } from 'src/components/shared/modal'
import { Button } from 'src/components/shared/Button'
import { Opportunity } from 'src/interfaces/opportunity'
import { colors } from 'src/theme'
import { showToast } from 'src/utils/toast'

interface Props {
  opportunity: Opportunity
  onClose(): void
}

export const ShareOpportunityModalContent = ({ opportunity, onClose }: Props) => {
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(opportunity.applicationUrl)
      showToast('Application link copied', { variant: 'success' })
    } catch {
      showToast('Could not copy the link. Select it and copy manually.', { variant: 'error' })
    }
  }, [opportunity.applicationUrl])

  const marketTitles = opportunity.markets
    .map((market) => {
      return market.title
    })
    .join(', ')

  return (
    <div css={tw`max-w-[528px]`} data-testid="opportunities-share-modal">
      <BasicModalHeader title={`${opportunity.title} is live`} onClose={onClose} />
      <BasicModalDescription
        title="Share the application link"
        icon={<LinkMinor width={20} height={20} />}
        description="Anyone with this link can apply. Applications arrive in your candidate pool — nothing starts onboarding until you make an offer."
      />
      <Divider css={tw`mt-4 mx-4 border-[#EDEDED]`} />

      <div css={tw`p-4`}>
        <div
          css={tw`bg-[#F6F6F6] border border-solid border-[#EDEDED] rounded-lg p-4 overflow-x-auto`}
        >
          <Typography variant="body2" color={colors.SHADES_GREY_500} css={tw`whitespace-nowrap`}>
            {opportunity.applicationUrl}
          </Typography>
        </div>
        <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mt-2`}>
          Covers {marketTitles}.
          {opportunity.markets.length > 1 ? ' Candidates choose their market on the form.' : ''}
        </Typography>
      </div>

      <Divider css={tw`mx-4 border-[#EDEDED]`} />
      <div css={tw`px-4 py-3 flex justify-between items-center`}>
        <Button className={tw`px-8`} size="small" color="grey" onClick={onClose}>
          Done
        </Button>
        <Button
          className={tw`px-8`}
          color="dark"
          size="small"
          data-testid="opportunities-share-copy"
          onClick={handleCopy}
        >
          Copy link
        </Button>
      </div>
    </div>
  )
}
